import * as Sentry from '@sentry/react'
import { isAxiosError } from 'axios'
import { isAuthApiError } from '@supabase/supabase-js'

/**
 * HTTP statuses that represent expected, user-facing outcomes — validation
 * (400/422), auth (401/403), not-found (404), conflict (409), rate-limit (429).
 * These are already surfaced to the user via a toast and are NOT incidents, so
 * we don't want them polluting Sentry. Everything else (5xx, network failures,
 * timeouts, unexpected JS errors) is a real error worth an alert.
 */
const EXPECTED_STATUS_CODES = new Set([400, 401, 403, 404, 409, 422, 429])

/**
 * API error codes whose 5xx is a designed, toasted outcome rather than an
 * incident. `TICKETS_UNAVAILABLE` (`TICKETS_UNAVAILABLE_CODE` in
 * `hooks/tickets/useCreateTicket.ts`): the 503 of « Signaler » on an API
 * without `TICKETS_GITHUB_REPO` — staging's and local dev's normal state.
 */
const EXPECTED_ERROR_CODES = new Set(['TICKETS_UNAVAILABLE'])

/** Whether a react-query error is a real incident worth reporting to Sentry. */
export function shouldReportQueryError(error: unknown): boolean {
  if (isAxiosError<{ error?: unknown }>(error)) {
    // Request cancelled (component unmounted / navigated away) — noise.
    if (error.code === 'ERR_CANCELED') return false
    const status = error.response?.status
    // A response with an expected status: the user already saw a toast.
    if (status !== undefined && EXPECTED_STATUS_CODES.has(status)) return false
    const code = error.response?.data?.error
    if (typeof code === 'string' && EXPECTED_ERROR_CODES.has(code)) return false
    // No response (network error / timeout) or a 5xx / other status → report.
    return true
  }
  // Auth calls go client-side straight to GoTrue, so they reject with an
  // AuthApiError carrying the GoTrue status — a wrong password is a 400 the
  // user already saw as a toast, not an incident. Retryable fetch failures
  // (status 0) and 5xx still report.
  if (isAuthApiError(error)) return !EXPECTED_STATUS_CODES.has(error.status)
  // Non-Axios error thrown inside a query/mutation fn → always report.
  return true
}

/**
 * Forward handled react-query errors to Sentry.
 *
 * React-query catches query and mutation rejections, so they never surface as
 * unhandled rejections and Sentry's auto-instrumentation never sees them —
 * without this bridge a failed create (5xx / dropped request) shows the user a
 * toast but leaves no trace in monitoring. Wired into the QueryClient's
 * QueryCache/MutationCache onError. Safe no-op when Sentry isn't initialised
 * (no DSN configured, e.g. local/dev).
 */
export function reportQueryError(
  error: unknown,
  source: 'query' | 'mutation'
): void {
  if (!shouldReportQueryError(error)) return
  Sentry.captureException(error, { tags: { source: `react-query.${source}` } })
}
